import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiService } from '../services/apiService';
import { PageShell } from '../components/layout/PageShell';
// @mcc-dev-preview:import
import { parseDevState } from '../dev/devPreview';
import './EmailSent.css';

// @mcc-dev-preview:start dev-preview-types
type EmailSentDevState = 'default' | 'resend-success' | 'resend-error' | 'cooldown';

const EMAIL_SENT_DEV_STATES: EmailSentDevState[] = [
  'default',
  'resend-success',
  'resend-error',
  'cooldown',
];

const DEV_PREVIEW_EMAIL = 'alex.preview@example.com';
// @mcc-dev-preview:end dev-preview-types

const EmailSent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const devState = parseDevState(searchParams, EMAIL_SENT_DEV_STATES);
  const [email, setEmail] = useState<string>('');
  const [isResending, setIsResending] = useState<boolean>(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState<string>('');
  const [lastEmailSent, setLastEmailSent] = useState<number | null>(null);
  const [timeUntilNextEmail, setTimeUntilNextEmail] = useState<number>(0);

  // @mcc-dev-preview:start dev-preview-seed
  useEffect(() => {
    if (!devState) return;

    setEmail(DEV_PREVIEW_EMAIL);

    switch (devState) {
      case 'default':
        break;
      case 'resend-success':
        setResendStatus('success');
        setResendMessage('Verification email sent successfully!');
        break;
      case 'resend-error':
        setResendStatus('error');
        setResendMessage(
          searchParams.get('errorMessage') ?? 'Failed to send verification email'
        );
        break;
      case 'cooldown': {
        const now = Date.now();
        setLastEmailSent(now);
        setTimeUntilNextEmail(5 * 60 * 1000);
        break;
      }
      default:
        break;
    }
  }, [devState, searchParams]);
  // @mcc-dev-preview:end dev-preview-seed

  useEffect(() => {
    if (devState) return;

    if (location.state && (location.state as { email?: string }).email) {
      setEmail((location.state as { email: string }).email);
    } else {
      navigate('/new-customer?cardId=' + (location.state as { cardId?: string })?.cardId);
    }

    const storedTimestamp = localStorage.getItem(`lastEmailSent_${email}`);
    if (storedTimestamp) {
      setLastEmailSent(parseInt(storedTimestamp, 10));
    }
  }, [location.state, navigate, email, devState]);

  useEffect(() => {
    if (!lastEmailSent) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastEmail = now - lastEmailSent;
      const fiveMinutes = 5 * 60 * 1000;
      const remainingTime = Math.max(0, fiveMinutes - timeSinceLastEmail);

      setTimeUntilNextEmail(remainingTime);

      if (remainingTime === 0) {
        setLastEmailSent(null);
        localStorage.removeItem(`lastEmailSent_${email}`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lastEmailSent, email]);

  const handleResendEmail = async () => {
    if (devState) return;
    if (!email || isResending) return;

    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    if (lastEmailSent && now - lastEmailSent < fiveMinutes) {
      setResendStatus('error');
      setResendMessage('Please wait before sending another email');
      return;
    }

    try {
      setIsResending(true);
      setResendStatus('idle');
      setResendMessage('');

      const cardId = (location.state as { cardId?: string })?.cardId || '';
      const returnTo: string | undefined = (location.state as { returnTo?: string })?.returnTo;
      const isValidationEmail: boolean = !!(location.state as { isValidationEmail?: boolean })?.isValidationEmail;

      const response = isValidationEmail
        ? await ApiService.sendValidationEmail(email, cardId, returnTo)
        : await ApiService.sendVerificationEmail(email, cardId);

      if (response.success) {
        setResendStatus('success');
        setResendMessage(
          isValidationEmail ? 'Validation email sent successfully!' : 'Verification email sent successfully!'
        );
        setLastEmailSent(now);
        localStorage.setItem(`lastEmailSent_${email}`, now.toString());
      } else {
        setResendStatus('error');
        setResendMessage(response.message || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Error resending email:', error);
      setResendStatus('error');
      setResendMessage('Network error. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToEmail = () => {
    const cardId = (location.state as { cardId?: string })?.cardId;
    navigate(cardId ? `/new-customer?cardId=${cardId}` : '/new-customer');
  };

  const formatTime = (milliseconds: number): string => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const canResendEmail = (): boolean => {
    if (!lastEmailSent) return true;
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    return now - lastEmailSent >= fiveMinutes;
  };

  const maskEmail = (emailAddr: string): string => {
    if (!emailAddr) return '';
    const [localPart, domain] = emailAddr.split('@');
    if (localPart.length <= 2) return emailAddr;

    const maskedLocal =
      localPart.charAt(0) +
      '*'.repeat(localPart.length - 2) +
      localPart.charAt(localPart.length - 1);
    return `${maskedLocal}@${domain}`;
  };

  return (
    <PageShell className="email-sent-container">
      <h1 className="ev-display-headline ev-animate-in">CHECK YOUR EMAIL</h1>
      <p className="ev-display-subtitle ev-animate-in">
        We&apos;ve sent a verification link to your email address.
      </p>

      <div className="ev-shell-card es-card ev-animate-in">
        <div className="es-card-inner">
          <div className="es-email-icon" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--ev-btn-primary)" strokeWidth="1.5">
              <path d="M4 6h16v12H4V6z" />
              <path d="M4 7l8 6 8-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="es-email-display">{maskEmail(email)}</div>
          <p className="es-instructions">
            Click the link in the email to verify your account and continue with the setup process.
          </p>

          <div className="es-actions">
            <button
              onClick={handleResendEmail}
              className="ev-btn-primary"
              disabled={isResending || !canResendEmail()}
            >
              {isResending
                ? 'Sending...'
                : !canResendEmail()
                  ? `Resend in ${formatTime(timeUntilNextEmail)}`
                  : 'Didn’t receive code? Resend'}
            </button>
            <button type="button" onClick={handleBackToEmail} className="ev-btn-secondary" aria-label="Use different email">
              Use different email
            </button>
          </div>

          {!canResendEmail() && timeUntilNextEmail > 0 && (
            <div className="es-rate-limit">
              <p>Please wait {formatTime(timeUntilNextEmail)} before sending another email</p>
            </div>
          )}

          {resendStatus !== 'idle' && (
            <div className={`es-resend-status es-resend-status-${resendStatus}`}>
              {resendStatus === 'success' && <span>{resendMessage}</span>}
              {resendStatus === 'error' && <span>{resendMessage}</span>}
            </div>
          )}

          <div className="es-help">
            <h3 className="es-help-title">Didn&apos;t receive the email?</h3>
            <ul className="es-help-list">
              <li>Check your spam/junk folder</li>
              <li>Make sure you entered the correct email address</li>
              <li>Wait a few minutes for the email to arrive</li>
              <li>Try resending the verification email</li>
            </ul>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default EmailSent;
